import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsSingleSelect, { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SettingsSlider from "components/settingsConfig/uiComponents/SettingsSlider";
import { Alert, AlertTitle } from "@mui/material";
import { AutoWidgetQueryBehavior, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";

const requestSize = 250;

const defaultWidgetRequest = {
  name: 'embed-list',
  endpoint: 'query/records',
  queryParams: { },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultEmbedListRequests(): WidgetRequest[] | undefined  {
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = {
    records: {
      size: requestSize
    },
    query: {
      op: "and",
      values: [
        {
          op: "contains",
          field: "site",
          values: requestAllowedSites,
        },
      ],
    },
  };
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

const requestAllowedSites = [
  "twitter.com",
  "instagram.com",
  // "youtube.com",
  "tiktok.com",
  "reddit.com"
]

export type EmbedsSettingStateType = {
  columns: number;
  spacing: number;
  pageSize: number;
  sortField: string;
  sortDirection: string;
}

export const defaultSettings: EmbedsSettingStateType = {
  columns: 1,
  spacing: 2,
  pageSize: 5,
  sortField: "",
  sortDirection: "desc"
}

export default function EmbedListSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  const [settingsState, setSettingsState] = useState<EmbedsSettingStateType> ({ ...defaultSettings, ...props.widget?.data?.settings});
  const settingsOnChange = (updatedState: EmbedsSettingStateType) => setSettingsState(updatedState);

  const columnOptions: OptionsValueType[] = useMemo(() => (
    [
      { label: "1", value: 1 },
      { label: "2", value: 2 },
      { label: "3", value: 3 },
      { label: "4", value: 4 },
      { label: "5", value: 5 },
      { label: "6", value: 6 }
    ]
    ), []);

    const spacingOptions: OptionsValueType[] = useMemo(() => (
      [
        { label: i18n.t("widget-types.embed-list.settings.spacing-compact"), value: 1 },
        { label: i18n.t("widget-types.embed-list.settings.spacing-comfortable"), value: 2 },
        { label: i18n.t("widget-types.embed-list.settings.spacing-spacious"), value: 3 }
      ]
    ), []);

    const sortingDirectionOptions: OptionsValueType[] = useMemo(() => (
      [
        { label: i18n.t("descending"), value: "desc" },
        { label: i18n.t("ascending"), value: "asc" }
      ]
    ), []);

    const sortingFieldOptions: OptionsValueType[] = useMemo(() => {
    if (!props.queryInfo?.fields) return [];
    // Only include fields with type integer, decimal, or timestamp
    const allowedTypes = ["integer", "decimal", "timestamp"];

    // Filter down to only fields that are of allowed types and support querying (Excludes Records/Social Universe)
    const queryFields = props.queryInfo.fields.filter(field => allowedTypes.includes(field.type) && field.query_supported !== false)
      .map(field => ({
        label: field.label,
        value: field.id,
      }));

    return [
      { label: i18n.t("none"), value: "" },
      ...queryFields
    ];
  }, [props.queryInfo]);

  const updateWidget = () => {
    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = {
      records: {
        size: requestSize,
        sort: [],
      },
      query: {
        op: "and",
        values: [
        {
          op: "contains",
          field: "site",
          values: requestAllowedSites,
        },
        ],
      },
    };
    if (settingsState.sortField !== "") {
      requestInitBody.records.sort = [{field: settingsState.sortField, direction: settingsState.sortDirection}];
    }

    widgetRequest.requestInit = { body: requestInitBody };
    props.saveWidgetDataAndRequests({
      settings: settingsState
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    });
  }


  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      maxWidth="sm"
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("display")]}
      title={i18n.t("widget-types.embed-list.settings.title")}
    >
      <>
        {tabValue === 0 &&
          <SplitSettingsWrapper>
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-types.embed-list.settings.layout")} showBottomDivider={true}>
                <SettingsSingleSelect
                  keyid="columns"
                  label={i18n.t("widget-types.embed-list.settings.columns")}
                  options={columnOptions}
                  value={columnOptions.find((option: OptionsValueType) => option.value === settingsState.columns)}
                  defaultValue={columnOptions.find((option: OptionsValueType) => (option.value === 1)) ?? columnOptions[0]}
                />
                <SettingsSingleSelect
                  keyid="spacing"
                  label={i18n.t("widget-types.embed-list.settings.spacing")}
                  options={spacingOptions}
                  value={spacingOptions.find((option: OptionsValueType) => option.value === settingsState.spacing)}
                  defaultValue={spacingOptions.find((option: OptionsValueType) => (option.value === 2)) ?? spacingOptions[1]}
                />
                <SettingsSlider
                  keyid="pageSize"
                  label={i18n.t("widget-types.embed-list.settings.page-size")}
                  min={1}
                  max={40}
                  step={1}
                  sliderValue={settingsState.pageSize}
                  defaultValue={5}
                  showCurrentValue={true}
                />
                <Alert severity="info" variant="outlined"><AlertTitle>{i18n.t("widget-types.embed-list.alerts.slider")}</AlertTitle></Alert>
              </SettingsAccordion>
              <SettingsAccordion label={i18n.t("widget-types.embed-list.settings.sorting")} showBottomDivider={false}>
                <SettingsSingleSelect
                  keyid="sortField"
                  label={i18n.t("widget-types.embed-list.settings.sorting-field")}
                  options={sortingFieldOptions}
                  value={sortingFieldOptions.find((option: OptionsValueType) => option.value === settingsState.sortField)}
                  defaultValue={sortingFieldOptions.find((option: OptionsValueType) => (option.value === "")) ?? sortingFieldOptions[0]}
                />
                {settingsState.sortField !== "" && (
                  <SettingsSingleSelect
                    keyid="sortDirection"
                    label={i18n.t("widget-types.embed-list.settings.sorting-direction")}
                    options={sortingDirectionOptions}
                    value={sortingDirectionOptions.find((option: OptionsValueType) => option.value === settingsState.sortDirection)}
                    defaultValue={sortingDirectionOptions.find((option: OptionsValueType) => (option.value === "desc")) ?? sortingDirectionOptions[1]}
                  />
                )}
              </SettingsAccordion>
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
      </>
    </TabbedDialog>
  )
}
